import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Heart, Baby, Ambulance, Activity, Pill } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { useBrand } from "@/config/brands";

interface ScenarioSearchProps {
  onSearch?: (query: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

interface Scenario {
  id: string;
  uuid: string;
  slug: string;
  name: string;
  description: string;
  icon: string;
  totalProcedures: number;
  estimatedCost: number;
  duration: string;
  severityLevel?: number;
  tags?: string[];
}

// Hardcoded scenarios for now - in the future this could come from a database
const scenarios: Scenario[] = [
  {
    id: "normal-delivery",
    uuid: "550e8400-e29b-41d4-a716-446655440001",
    slug: "normal-pregnancy-delivery",
    name: "Normal Pregnancy & Delivery",
    description: "Complete prenatal care through normal vaginal delivery",
    icon: "baby",
    totalProcedures: 12,
    estimatedCost: 28500,
    duration: "9 months",
    severityLevel: 2,
    tags: ["pregnancy", "uncomplicated", "vaginal-delivery"],
  },
  {
    id: "c-section",
    uuid: "550e8400-e29b-41d4-a716-446655440002",
    slug: "c-section-delivery",
    name: "C-Section Delivery",
    description: "Cesarean section delivery with standard prenatal care",
    icon: "baby",
    totalProcedures: 18,
    estimatedCost: 45200,
    duration: "9 months + recovery",
    severityLevel: 4,
    tags: ["pregnancy", "cesarean", "surgery"],
  },
  {
    id: "heart-attack-er",
    uuid: "550e8400-e29b-41d4-a716-446655440003",
    slug: "heart-attack-emergency",
    name: "Heart Attack Emergency",
    description: "ER treatment for acute myocardial infarction",
    icon: "heart",
    totalProcedures: 25,
    estimatedCost: 85000,
    duration: "1-2 weeks",
    severityLevel: 9,
    tags: ["emergency", "cardiac", "critical"],
  },
  {
    id: "broken-arm",
    uuid: "550e8400-e29b-41d4-a716-446655440004",
    slug: "broken-arm-treatment",
    name: "Broken Arm Treatment",
    description: "ER visit, X-rays, casting and follow-up",
    icon: "activity",
    totalProcedures: 8,
    estimatedCost: 12000,
    duration: "6-8 weeks",
    severityLevel: 3,
    tags: ["emergency", "orthopedic", "fracture"],
  },
  {
    id: "appendectomy",
    uuid: "550e8400-e29b-41d4-a716-446655440005",
    slug: "appendectomy-surgery",
    name: "Appendectomy Surgery",
    description: "Emergency appendix removal surgery",
    icon: "ambulance",
    totalProcedures: 15,
    estimatedCost: 38000,
    duration: "3-5 days",
    severityLevel: 6,
    tags: ["emergency", "surgery", "appendicitis"],
  },
  {
    id: "diabetes-management",
    uuid: "550e8400-e29b-41d4-a716-446655440006",
    slug: "diabetes-annual-care",
    name: "Diabetes Annual Management",
    description: "Year of diabetes monitoring and treatment",
    icon: "pill",
    totalProcedures: 20,
    estimatedCost: 15000,
    duration: "12 months",
    severityLevel: 5,
    tags: ["chronic", "diabetes", "monitoring"],
  },
];

export function ScenarioSearch({
  onSearch,
  placeholder = "Search medical scenarios...",
  autoFocus = false,
}: ScenarioSearchProps) {
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredScenarios, setFilteredScenarios] = useState<Scenario[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const brand = useBrand();

  // Filter scenarios based on query
  useEffect(() => {
    if (query.length >= 2) {
      const searchLower = query.toLowerCase();
      const filtered = scenarios.filter(
        (scenario) =>
          scenario.name.toLowerCase().includes(searchLower) ||
          scenario.description.toLowerCase().includes(searchLower) ||
          scenario.tags?.some((tag) => tag.toLowerCase().includes(searchLower))
      );
      setFilteredScenarios(filtered);
      setShowDropdown(filtered.length > 0);
    } else {
      setFilteredScenarios([]);
      setShowDropdown(false);
    }
  }, [query]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || filteredScenarios.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredScenarios.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev <= 0 ? filteredScenarios.length - 1 : prev - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && filteredScenarios[selectedIndex]) {
          handleSelectScenario(filteredScenarios[selectedIndex]);
        } else {
          handleSubmit(e);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSelectScenario = (scenario: Scenario) => {
    setQuery(scenario.name);
    setShowDropdown(false);
    setSelectedIndex(-1);
    navigate(`/scenarios/${scenario.uuid}/${scenario.slug}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setIsLoading(true);
      setShowDropdown(false);
      if (onSearch) {
        onSearch(query.trim());
      } else {
        navigate(`/scenarios?search=${encodeURIComponent(query.trim())}`);
      }
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  // Handle clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get icon component
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "baby":
        return <Baby className="w-4 h-4" />;
      case "heart":
        return <Heart className="w-4 h-4" />;
      case "ambulance":
        return <Ambulance className="w-4 h-4" />;
      case "activity":
        return <Activity className="w-4 h-4" />;
      case "pill":
        return <Pill className="w-4 h-4" />;
      default:
        return <Heart className="w-4 h-4" />;
    }
  };

  // Get severity color
  const getSeverityColor = (level?: number) => {
    if (!level) return "text-gray-400";
    if (level <= 3) return "text-green-400";
    if (level <= 6) return "text-yellow-400";
    return "text-red-400";
  };

  // Format price
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="relative w-full">
      <form onSubmit={handleSubmit} className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Heart className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoFocus={autoFocus}
              className="w-full pl-10 pr-4 py-3 terminal-input text-base"
            />
          </div>
          <Button
            type="submit"
            disabled={isLoading}
            className={`${brand.searchButtonBg} ${brand.searchButtonHover} ${brand.textColor} font-mono px-3 sm:px-4`}
          >
            {isLoading ? (
              <span className="loading-dots text-xs sm:text-sm">Searching</span>
            ) : (
              <>
                <Search className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Search</span>
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Autocomplete dropdown */}
      {showDropdown && filteredScenarios.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-[9999] w-full mt-2 bg-black/95 backdrop-blur-sm border border-green-500/20 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="max-h-[400px] overflow-y-auto">
            {filteredScenarios.map((scenario, index) => (
              <div
                key={scenario.id}
                className={`p-4 cursor-pointer border-b border-green-500/10 hover:bg-green-500/10 transition-colors ${
                  index === selectedIndex ? "bg-green-500/20" : ""
                }`}
                onClick={() => handleSelectScenario(scenario)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div
                      className={`p-2 rounded-lg bg-green-500/10 ${getSeverityColor(
                        scenario.severityLevel
                      )}`}
                    >
                      {getIcon(scenario.icon)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-green-400 font-semibold">
                          {scenario.name}
                        </span>
                        {scenario.severityLevel && (
                          <Badge
                            className={`text-xs bg-black/50 ${getSeverityColor(
                              scenario.severityLevel
                            )}`}
                          >
                            Severity {scenario.severityLevel}/10
                          </Badge>
                        )}
                      </div>
                      <div className="text-gray-400 text-sm">
                        {scenario.description}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>{scenario.totalProcedures} procedures</span>
                        <span>{scenario.duration}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-green-400 font-mono font-semibold">
                      {formatPrice(scenario.estimatedCost)}
                    </div>
                    <div className="text-xs text-gray-500">Estimated</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}